import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Bot,
  Cpu,
  MessagesSquare,
  Plus,
  Play,
  RotateCcw,
  MoreHorizontal,
  CheckCircle2,
  Circle,
  Users,
  MessageSquare,
  AlertTriangle,
  Loader2,
  Square,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { api, ApiError } from '@/api/client';
import { useAgentAction } from '@/hooks/useAgents';
import { systemApi } from '@/api/system';
import type { AgentListResponse, AgentSummary, DashboardStats } from '@/api/types';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EmptyState } from '@/components/EmptyState';
import { StatusBadge } from '@/components/StatusBadge';
import { CardSkeleton, StatCardSkeleton } from '@/components/LoadingSkeleton';
import { MigrationDialog } from '@/components/MigrationDialog';
import { OnboardingHero } from '@/components/OnboardingHero';
import { useMigrationStatus } from '@/hooks/useMigration';
import { formatBytes, formatRelative } from '@/lib/format';

/**
 * Dashboard — §4.3. Four stat cards across the top, onboarding checklist
 * when we have no agents, and an agent grid below. Stats and agents are
 * loaded independently so the grid renders as soon as either is ready.
 */
export default function Dashboard() {
  const statsQuery = useQuery<DashboardStats>({
    queryKey: ['system', 'stats'],
    queryFn: systemApi.stats,
    refetchInterval: 30_000,
    retry: false,
  });

  const agentsQuery = useQuery<AgentListResponse>({
    queryKey: ['agents'],
    queryFn: () => api<AgentListResponse>('/agents'),
    retry: false,
  });

  // Migration — hit once per session. A 404 on older backends is fine
  // (the hook sets `retry: false`) so we just fall through to the
  // normal dashboard when migrationQuery.isError is true.
  const migrationQuery = useMigrationStatus();
  const migrationPending = !!(
    migrationQuery.data?.legacy_agents_detected &&
    !migrationQuery.data?.already_migrated
  );
  const [migrationDialogOpen, setMigrationDialogOpen] = useState(false);
  const [migrationAutoOpened, setMigrationAutoOpened] = useState(false);
  const [migrationDismissed, setMigrationDismissed] = useState(false);

  // Auto-open the dialog on the first render where we see legacy agents.
  useEffect(() => {
    if (migrationPending && !migrationAutoOpened && !migrationDismissed) {
      setMigrationDialogOpen(true);
      setMigrationAutoOpened(true);
    }
  }, [migrationPending, migrationAutoOpened, migrationDismissed]);

  function handleMigrationDialogChange(next: boolean) {
    setMigrationDialogOpen(next);
    if (!next) setMigrationDismissed(true);
  }

  const agents = agentsQuery.data?.items ?? [];
  const hasAgents = agents.length > 0;
  const isAgentsLoading = agentsQuery.isLoading;
  const isStatsLoading = statsQuery.isLoading;

  // Zero-state: show OnboardingHero when there are no agents AND no
  // legacy migration is pending. Otherwise, the normal dashboard
  // (with stat cards, migration banner, etc.) renders.
  const showOnboardingHero =
    !isAgentsLoading && !hasAgents && !migrationPending && !agentsQuery.isError;

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
        <p className="text-sm text-muted-foreground">
          AgentHub status at a glance.
        </p>
      </div>

      {/* Migration banner — shown when the user dismissed the dialog
          but legacy agents are still sitting in config.yaml. */}
      {migrationPending && migrationDismissed && !migrationDialogOpen && (
        <div
          className="flex items-center gap-3 rounded-md border border-warning/40 bg-warning/10 px-4 py-2 text-sm"
          role="status"
        >
          <AlertTriangle className="h-4 w-4 text-warning" aria-hidden />
          <div className="flex-1">
            <p className="font-medium">
              Legacy agents detected in your config.yaml.
            </p>
            <p className="text-xs text-muted-foreground">
              You can import them into the panel at any time.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMigrationDialogOpen(true)}
          >
            Review
          </Button>
        </div>
      )}

      {showOnboardingHero ? (
        <OnboardingHero />
      ) : (
        <>
          <section
            aria-label="System statistics"
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4"
          >
            {isStatsLoading ? (
              <>
                <StatCardSkeleton />
                <StatCardSkeleton />
                <StatCardSkeleton />
                <StatCardSkeleton />
              </>
            ) : (
              <>
                <StatCard
                  icon={Users}
                  label="Agents"
                  value={
                    statsQuery.data
                      ? `${statsQuery.data.agents_active} / ${statsQuery.data.agents_total} active`
                      : '—'
                  }
                />
                <StatCard
                  icon={Bot}
                  label="Discord bots online"
                  value={
                    statsQuery.data
                      ? `${statsQuery.data.discord_bots_online} connected`
                      : '—'
                  }
                />
                <StatCard
                  icon={MessagesSquare}
                  label="Claude API today"
                  value={
                    statsQuery.data
                      ? statsQuery.data.claude_api_requests_today.toLocaleString()
                      : '—'
                  }
                  hint="requests"
                />
                <StatCard
                  icon={Cpu}
                  label="Server"
                  value={
                    statsQuery.data
                      ? `CPU ${Math.round(statsQuery.data.server.cpu_pct)}%`
                      : '—'
                  }
                  hint={
                    statsQuery.data
                      ? `RAM ${formatBytes(statsQuery.data.server.ram_used_bytes)} / ${formatBytes(statsQuery.data.server.ram_total_bytes)}`
                      : undefined
                  }
                />
              </>
            )}
          </section>

          {!isAgentsLoading && !hasAgents && !migrationPending ? <OnboardingChecklist /> : null}

          <section aria-label="Agents">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-lg font-semibold tracking-tight">Your agents</h2>
              <Button asChild size="sm" variant="outline">
                <Link to="/agents/new">
                  <Plus className="h-4 w-4" aria-hidden />
                  <span>New agent</span>
                </Link>
              </Button>
            </div>

            {isAgentsLoading ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                <CardSkeleton />
                <CardSkeleton />
                <CardSkeleton />
              </div>
            ) : agentsQuery.isError ? (
              <EmptyState
                title="Couldn't load agents"
                description={
                  agentsQuery.error instanceof ApiError
                    ? (agentsQuery.error.detail ?? agentsQuery.error.title)
                    : 'Check the panel backend and try again.'
                }
                action={
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => agentsQuery.refetch()}
                  >
                    Retry
                  </Button>
                }
              />
            ) : hasAgents ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {agents.map((agent) => (
                  <AgentCard key={agent.name} agent={agent} />
                ))}
              </div>
            ) : (
              <EmptyState
                icon={Users}
                title="Create your first agent"
                description="Agents connect Claude to Discord. Spin one up in under a minute."
                action={
                  <Button asChild>
                    <Link to="/agents/new">
                      <Plus className="h-4 w-4" aria-hidden />
                      <span>New agent</span>
                    </Link>
                  </Button>
                }
              />
            )}
          </section>
        </>
      )}

      {migrationQuery.data && (
        <MigrationDialog
          open={migrationDialogOpen}
          onOpenChange={handleMigrationDialogChange}
          status={migrationQuery.data}
        />
      )}
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" aria-hidden />
      </CardHeader>
      <CardContent>
        <div className="text-xl font-semibold tracking-tight">{value}</div>
        {hint ? (
          <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function AgentCard({ agent }: { agent: AgentSummary }) {
  const action = useAgentAction();

  async function handleAction(kind: 'start' | 'stop' | 'restart') {
    try {
      await action.mutateAsync({ name: agent.name, action: kind });
    } catch {}
  }

  return (
    <Card className="transition-colors hover:border-primary/40">
      <CardHeader className="flex-row items-start justify-between space-y-0 pb-2">
        <div className="min-w-0">
          <CardTitle className="truncate text-base">
            <Link to={`/agents/${agent.name}`} className="hover:text-primary">
              {agent.name}
            </Link>
          </CardTitle>
          {agent.description ? (
            <CardDescription className="line-clamp-2">
              {agent.description}
            </CardDescription>
          ) : null}
        </div>
        <StatusBadge status={agent.status} />
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {agent.discord_enabled ? (
            <span className="inline-flex items-center gap-1">
              <MessageSquare className="h-3 w-3" aria-hidden />
              {agent.discord_connected ? 'Discord connected' : 'Discord offline'}
            </span>
          ) : (
            <span>Discord disabled</span>
          )}
          <span aria-hidden>·</span>
          <span>
            {agent.last_message_at
              ? `Last msg ${formatRelative(agent.last_message_at)}`
              : 'No messages yet'}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <Button asChild size="sm" variant="outline">
            <Link to={`/agents/${agent.name}`}>View</Link>
          </Button>
          <Button size="sm" variant="outline" disabled={action.isPending} onClick={() => handleAction('restart')}>
            {action.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <RotateCcw className="h-3.5 w-3.5" aria-hidden />}
            Restart
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="More actions">
                <MoreHorizontal className="h-4 w-4" aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to={`/agents/${agent.name}`}>Open detail</Link>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleAction('start')}>
                <Play className="h-4 w-4" aria-hidden />
                Start
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleAction('restart')}>
                <RotateCcw className="h-4 w-4" aria-hidden />
                Restart
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleAction('stop')}>
                <Square className="h-4 w-4" aria-hidden />
                Stop
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  );
}

/** Onboarding checklist — §4.3. Hidden once at least one agent exists. */
function OnboardingChecklist() {
  const items = [
    { label: 'Admin password set', done: true },
    { label: 'First agent created', done: false },
    { label: 'Discord bot online', done: false },
    { label: 'First user message received', done: false },
  ];
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Get started</CardTitle>
        <CardDescription>
          A few quick steps to finish setting up AgentHub.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.map((item) => {
          const Icon = item.done ? CheckCircle2 : Circle;
          return (
            <div key={item.label} className="flex items-center gap-2 text-sm">
              <Icon
                className={
                  item.done
                    ? 'h-4 w-4 text-success'
                    : 'h-4 w-4 text-muted-foreground'
                }
                aria-hidden
              />
              <span
                className={item.done ? 'text-muted-foreground line-through' : ''}
              >
                {item.label}
              </span>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
